import { Alert, Flex, Form, Spin, Typography } from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { ReturnButton } from "ui";
import { ContractForm } from "components";
import { routes } from "constants/routes";
import { CONTRACT_STATUS_ENUM, CreateContractRequest, NOTIFICATION_TYPE_ENUM } from "interfaces";
import { editContract, getStatusMessage } from "services";
import { useStores } from "stores";
import { dateISOFormat, parseErrorMessage, removeSpaces } from "utils";
import { useNotificationContext } from "providers";

export const ContractEditPage = observer(() => {
  const { contractCard } = useStores();
  const { id } = useParams();
  const navigate = useNavigate();
  const { openNotification } = useNotificationContext();

  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    if (contractCard && id && contractCard.card.id !== id) {
      contractCard.fetchCardById(id);
    }
  }, [id, contractCard]);

  const closeResultModal = () => {
    return Promise.resolve(navigate(`/${routes.contracts}/${contractCard.card.id}`));
  };

  const saveContract = async () => {
    setLoading(true);
    const values: CreateContractRequest = form.getFieldsValue();
    const formData = {
      ...values,
      date: dateISOFormat(values.date),
      fiatAmount: values.fiatAmount,
      unk: contractCard.card.status === CONTRACT_STATUS_ENUM.ACTIVE ? undefined : removeSpaces(values.unk),
    };

    await editContract({ id: contractCard.card.id }, formData)
      .then(() => {
        openNotification({
          message: "Договор успешно отредактирован",
          type: NOTIFICATION_TYPE_ENUM.SUCCESS,
        });
        setTimeout(() => {
          setLoading(false);
          closeResultModal();
        }, 5000);
      })
      .catch((e) => {
        openNotification({
          description: parseErrorMessage(e),
          type: NOTIFICATION_TYPE_ENUM.ERROR,
        });
        setLoading(false);
      });
  };

  const initialValues = {
    ...contractCard.card,
    counterpartyLegalAddress: contractCard.card.counterpartyLegalAddress ?? "",
    counterpartyActualAddress: contractCard.card.counterpartyActualAddress ?? "",
    foreignOrganizationCode: contractCard.card.foreignOrganizationCode ?? "",
    fiatAmount: contractCard.card.fiatAmount,
    date: dayjs(contractCard.card.date),
  };

  return (
    <Spin spinning={contractCard.isPending}>
      {contractCard.fetchStatus === "success" && (
        <Flex vertical>
          <Flex align="center" gap="8px">
            <ReturnButton onClick={closeResultModal} disabled={loading} loading={loading} className="bg-basic-300" />
            <Typography.Title>Редактировать данные договора</Typography.Title>
          </Flex>
          <Flex vertical gap="large">
            <ContractForm
              isEditPage
              initValues={initialValues}
              form={form}
              disabled={loading}
              onFinish={saveContract}
              contractInfo={contractCard.card}
            />
          </Flex>
        </Flex>
      )}
      {contractCard.fetchStatus === "error" && contractCard.fetchError && (
        <Alert
          message={getStatusMessage(contractCard.fetchError.status)}
          description={parseErrorMessage(contractCard.fetchError)}
          type="error"
          showIcon
        />
      )}
    </Spin>
  );
});
